'use client';
import React, {memo, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontSize, fontWeight, overlay, zIndex} from '@/styles/tokens';
import {useCountUp} from '@/hooks/useCountUp';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PublicIcon from '@mui/icons-material/Public';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

const ICON_MAP: Record<string, React.ReactNode> = {
    work: <WorkOutlineIcon sx={{fontSize: fontSize[32]}} />,
    public: <PublicIcon sx={{fontSize: fontSize[32]}} />,
    money: <AttachMoneyIcon sx={{fontSize: fontSize[32]}} />,
    people: <PeopleAltOutlinedIcon sx={{fontSize: fontSize[32]}} />
};

interface MilestoneItem {
    icon: string;
    stat: string;
    label: string;
    bg: string;
    iconColor: string;
}

interface MilestonesProps {
    milestonesData: MilestoneItem[];
}

// Parse stat string into prefix, number, suffix e.g. "$26.6M+" → {prefix:"$", value:26.6, suffix:"M+"}
function parseStat(stat: string): {prefix: string; value: number; suffix: string; decimals: number} {
    const match = stat.match(/^(\$?)(\d+\.?\d*)(.*)$/);
    if (!match) return {prefix: '', value: 0, suffix: stat, decimals: 0};
    const raw = match[2];
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    return {prefix: match[1], value: parseFloat(raw), suffix: match[3], decimals};
}

const MilestoneCard = ({item}: {item: MilestoneItem}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    const {prefix, value, suffix, decimals} = parseStat(item.stat);
    const count = useCountUp(value, decimals, active);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setActive(true);
                    observer.disconnect();
                }
            },
            {threshold: 0.4}
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={ref}
            sx={{
                background: item.bg,
                borderRadius: borderRadius['2xl'],
                p: {xs: 3, md: 4},
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                zIndex: zIndex.raised,
                height: '100%'
            }}
        >
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: borderRadius.lg,
                    background: overlay.whiteNear,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mx: 'auto',
                    color: item.iconColor
                }}
            >
                {ICON_MAP[item.icon]}
            </Box>

            <Typography
                className="font-headers"
                sx={{
                    fontSize: {xs: fontSize[32], md: fontSize[48]},
                    fontWeight: fontWeight.extrabold,
                    color: colors.text.inverse,
                    lineHeight: 1,
                    textAlign: 'center'
                }}
            >
                {prefix}
                {decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}
                {suffix}
            </Typography>

            <Typography
                className="font-regular-text"
                sx={{
                    fontSize: fontSize[16],
                    color: overlay.whiteLabel,
                    fontWeight: fontWeight.medium,
                    lineHeight: 1.5,
                    textAlign: 'center'
                }}
            >
                {item.label}
            </Typography>
        </Box>
    );
};

const Milestones = ({milestonesData}: MilestonesProps) => {
    return (
        <Box component="section" className="top-space mobile-top-padding">
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: fontSize[32], sm: fontSize[48]}} className="font-headers">
                        Debut Infotech at a Glance
                    </Typography>
                </Box>

                <Box sx={{position: 'relative'}}>
                    <Grid container spacing={{xs: 3, md: 4}} justifyContent="center">
                        {milestonesData.map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <MilestoneCard item={item} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(Milestones);
